import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { protocolApplies, splitEvenly } from '@dairy/domain';
import { newId, tryParseBirr, type EventRecord, type ProtocolType } from '@dairy/shared';
import { useSettings } from '@/auth/useSession';
import { DateField } from '@/components/DateField';
import { MoneyInput } from '@/components/inputs';
import { toast } from '@/components/toast';
import { Button, Card, Checkbox, Field, Input, PageHeader, Segmented, Select } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { rememberLastUsed, useHerd, useLastUsed, useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { saveEventsWithEffects } from './effects';

/** Screen 12 — Batch vaccination / deworming (HE-3): one event per animal, the total cost split evenly. */
export function BatchHealthPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const s = useSettings();
  const today = useToday();
  const navigate = useNavigate();
  const last = useLastUsed();
  const [params] = useSearchParams();
  const [type, setType] = useState<ProtocolType>((params.get('type') as ProtocolType) === 'DEWORMING' ? 'DEWORMING' : 'VACCINATION');
  const herd = useHerd(today);
  const protocols = useLiveQuery(() => db.healthProtocols.toArray(), []) ?? [];
  const [protocolId, setProtocolId] = useState(params.get('protocolId') ?? '');
  const [date, setDate] = useState(today);
  const [product, setProduct] = useState('');
  const [dose, setDose] = useState('');
  const [givenBy, setGivenBy] = useState(last.givenBy ?? '');
  const [total, setTotal] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const protocol = protocols.find((p) => p.id === protocolId);
  const candidates = useMemo(
    () => herd.rows.filter((r) => r.animal.status === 'ACTIVE' && (!protocol || protocolApplies(protocol, r.animal, r.stage, today, s))),
    [herd.rows, protocol, today, s],
  );
  const totalP = tryParseBirr(total);
  const shares = totalP.ok && totalP.santim ? splitEvenly(totalP.santim, selected.size) : [];

  const submit = async () => {
    if (!product.trim()) return setError(t('events.errors.product'));
    if (selected.size === 0) return setError(t('events.errors.noAnimals'));
    if (!totalP.ok) return setError(t('events.errors.cost'));
    const ids = candidates.filter((r) => selected.has(r.animal.id)).map((r) => r.animal.id);
    const events = ids.map(
      (animalId, i) =>
        ({
          id: newId(),
          animalId,
          type,
          date,
          data: { protocolId: protocolId || undefined, product: product.trim(), dose: dose.trim() || undefined, givenBy: givenBy.trim() || undefined },
          costSantim: shares[i] ?? null,
          isOpening: false,
          note: null,
        }) as unknown as EventRecord,
    );
    try {
      await saveEventsWithEffects(events);
      if (givenBy) await rememberLastUsed('givenBy', givenBy);
      toast(t('events.batchSaved', { count: events.length }));
      navigate(-1);
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-8">
      <PageHeader title={t('events.batchTitle')} />
      <Card className="space-y-4">
        <Segmented
          value={type}
          onChange={(v) => {
            setType(v);
            setProtocolId('');
          }}
          options={[
            { value: 'VACCINATION', label: t('events.type.VACCINATION') },
            { value: 'DEWORMING', label: t('events.type.DEWORMING') },
          ]}
        />
        <Field label={t('events.protocol')}>
          <Select
            value={protocolId}
            onChange={(e) => {
              setProtocolId(e.target.value);
              const p = protocols.find((x) => x.id === e.target.value);
              if (p?.product) setProduct(p.product);
              setSelected(new Set());
            }}
          >
            <option value="">{t('common.none')}</option>
            {protocols.filter((p) => p.type === type && p.active).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.date')}>
          <DateField value={date} max={today} onChange={setDate} />
        </Field>
        <Field label={t('events.product')}>
          <Input value={product} onChange={(e) => setProduct(e.target.value)} maxLength={80} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('events.dose')}>
            <Input value={dose} onChange={(e) => setDose(e.target.value)} maxLength={40} />
          </Field>
          <Field label={t('events.givenBy')}>
            <Input value={givenBy} onChange={(e) => setGivenBy(e.target.value)} maxLength={60} />
          </Field>
        </div>
        <Field label={t('events.totalCost')} hint={shares.length ? t('events.costShares', { shares: shares.map((x) => f.amount(x)).join(' / ') }) : undefined}>
          <MoneyInput value={total} onChange={(e) => setTotal(e.target.value)} />
        </Field>
      </Card>
      <Card>
        <div className="mb-2 flex items-center justify-between">
          <span className="font-semibold">{t('events.selectedCount', { count: selected.size })}</span>
          <Button size="sm" variant="secondary" onClick={() => setSelected(selected.size === candidates.length ? new Set() : new Set(candidates.map((r) => r.animal.id)))}>
            {selected.size === candidates.length ? t('common.selectNone') : t('common.selectAll')}
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
          {candidates.map((r) => (
            <Checkbox
              key={r.animal.id}
              label={r.animal.tag}
              checked={selected.has(r.animal.id)}
              onChange={(e) => {
                const n = new Set(selected);
                if (e.target.checked) n.add(r.animal.id);
                else n.delete(r.animal.id);
                setSelected(n);
              }}
            />
          ))}
        </div>
      </Card>
      {error ? <p className="text-red-700">{error}</p> : null}
      <Button size="lg" className="w-full" onClick={() => void submit()}>
        {t('common.save')}
      </Button>
    </div>
  );
}
